import React, {ReactNode} from 'react'
import {Box, BoxProps, Flex, Stack, Text} from '../../../../vanilla'
import {PDPCookHeatIcon, PDPCookTimeIcon} from '../../../icons'

interface CookingInstructionsProps extends BoxProps<'div'> {
  instructions?: {
    meth?: string
    num: string
    recommended: boolean
    headings: {name: string; text: string}[]
    steps: {num: string; txt: string}[]
  }[]
}

export const CookingInstructions = (props: CookingInstructionsProps) => {
  const {instructions, ...rest} = props

  if (!instructions || !instructions.length) {
    return null
  }

  return (
    <Stack spacing="28px" padding="20px" borderBottom="1px" borderColor="gray200" {...rest}>
      <Text variant="heading1">How to cook</Text>
      <Stack spacing="20px">
        {instructions.map(({meth, headings, steps}, idx) => (
          <Stack key={`${meth}-${idx}`} spacing="16px">
            {meth && <Text variant="text6">{meth.replace(/_/gi, ' ')}</Text>}
            {!!headings?.filter(({text, name}) => text && name)?.length && (
              <Box display="inline-block" width="full">
                {headings.map(({name, text}, idx) => (
                  <CookingHeading key={`${name}-${idx}`} text={text} name={name} index={idx} />
                ))}
              </Box>
            )}
            {steps && (
              <Stack spacing="12px">
                {steps.map(({txt}) => (
                  <Text variant="text3" color="gray1000" key={txt}>
                    {txt}
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

const cookingHeadingNameMap: Record<string, ReactNode> = {
  default: <PDPCookHeatIcon boxSize="12px" />,
  'Cooking Time': (
    <Flex alignItems="center" gap="4px">
      <PDPCookTimeIcon boxSize="12px" />
      <Text variant="text4">Cook Time</Text>
    </Flex>
  ),
} as const

const HeadingPreElement = ({name}: {name: string}): JSX.Element => (
  <>
    {
      cookingHeadingNameMap[
        Object.prototype.hasOwnProperty.call(cookingHeadingNameMap, name) ? name : 'default'
      ]
    }
  </>
)

interface CookingHeadingProps {
  text?: string
  name?: string
  index: number
}

const CookingHeading = ({text, name, index}: CookingHeadingProps) => {
  if (!text || !name) {
    return null
  }

  return (
    <>
      <Flex
        gap="4px"
        alignItems="center"
        display="inline-flex"
        marginRight="8px"
        marginBottom={(index + 1) % 2 === 0 ? '8px' : '0px'}
      >
        <HeadingPreElement name={name} />
        <Text variant="text3Bold" color={name === 'Cooking Time' ? 'gray800' : 'inherit'}>
          {text}
        </Text>
      </Flex>
      {(index + 1) % 2 === 0 ? <br /> : null}
    </>
  )
}
